import type { LocationQuery } from 'vue-router';
import { keepPreviousData } from '@tanstack/vue-query';
import type { ContactForm, ContactFormBody } from '#shared/contact/types';
import type { ProposalAnswerType } from '#shared/lead/type';

export function useFetchAgent(userName?: string | string[]) {
  const { locale } = useI18n();

  return useQuery({
    queryKey: [`AGENT`, locale, userName],
    queryFn: () => {
      return $fetch(`/api/agents/${userName}`, {
        method: 'GET',
        query: {
          locale: locale.value,
        },
      });
    },
    retry: 0,
  });
}

export function useSearchAgents(searchTerm: MaybeRef<string | undefined>) {
  const { locale } = useI18n();
  const searchTermRef = toRef(searchTerm);
  const enabled = computed(() => !!searchTermRef.value);
  return useQuery({
    queryKey: [`AGENTS_SEARCH`, locale, searchTerm],
    placeholderData: keepPreviousData,
    queryFn: () => {
      return $fetch(`/api/agents`, {
        method: 'GET',
        query: {
          searchTerm: searchTermRef.value,
          locale: locale.value,
        },
      });
    },
    retry: 0,
    enabled,
  });
}

export function useSearchSectorAgents(locationSlug: MaybeRef<string | undefined>, searchParams: Ref<LocationQuery>) {
  const { locale } = useI18n();
  const locationSlugRef = toRef(locationSlug);
  const enabled = computed(() => !!locationSlugRef.value);
  return useQuery({
    queryKey: [`AGENTS_BY_SECTOR`, locale, locationSlug, searchParams],
    placeholderData: keepPreviousData,
    queryFn: () => {
      return $fetch(`/api/agents/sector/${locationSlugRef.value}`, {
        method: 'GET',
        query: {
          ...searchParams.value,
          locale: locale.value,
        },
      });
    },
    retry: 0,
    enabled,
  });
}

export function useAgentContactForm(userName: Ref<string>) {
  return useMutation({
    mutationFn: (contactForm: ContactForm) => {
      const body: ContactFormBody = {
        contactForm,
        marketingTracking: getMarketingTracking(),
      };
      return $fetch(`/api/agents/${userName.value}/contact`, {
        body,
        method: 'POST',
      });
    },
  });
}

export function useAgentAnswerLeadProposal(answerType: ProposalAnswerType, token?: string | string[]) {
  return useQuery({
    queryKey: [`LEAD_REQUEST_ANSWER`, answerType, token],
    queryFn: () => {
      return $fetch(`/api/leads/${answerType}/${token}`, { method: 'GET', retry: 0 });
    },
    retry: false,
  });
}
